import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_USER_GROUPS_GRID } from '../../../../../../../../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../../../../../../../../grid/gridView';
import {
  Column,
  ColumnKeys,
  getGridColumnDetails,
  nameRenderer,
  descriptionRenderer,
  companyRenderer,
  getGridColumns,
  getPredefinedFilterKeys,
  getFilters,
} from '../../grid-config';
import { ColumnDetails } from '../../../../../../../../../../grid/types/common';
import { Filter } from '../../../../../../../../../../grid/gridFilters/types';
import { SelectOption } from '../../../../../../../../../../ui/gzSelect/types';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '../../../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView/configs/page-config';
import { GzUserGroupsDataProvider } from '../../GzExchangeProtectionUserGroupsDataProvider';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import {
  SELECTION_STORE_CONFIGURATION,
  SelectionStoreMode,
  ViewType,
} from '../../../../../../../../../../grid/gridView/types';
import { UserGroupGridItem, UserGroupItem } from '@nirvana/shared/dtos/types/policies/common';
import { GzButtonWebComponent } from '../../../../../../../../../../ui/gzButton';
import { GzImageRepoWebComponent } from '../../../../../../../../../../ui/gzImageRepo';
import { ImageState } from '../../../../../../../../../../ui/gzImageRepo/types';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindowComplex,
} from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzEditUserGroupContainerWebComponent } from '../../GzEditUserGroupContainerWebComponent';
import { ValidationResult } from '../../../../../../../../../../ui/gzForm/formField/types/form-validator';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from 'apps/webcomponents/src/ui/gzButton/types';
import { GzDialog } from '../../../../../../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../../../../../../ui/gzDialog';
import { GzToastNotificationWebComponent } from '../../../../../../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../../../../../../ui/gzToastNotification/type';

const VIEW_GRID_ID = 'policy-user-groups-grid';

/**
 * @summary Type here the description of this component
 * @tag GzExchangeProtectionUserGroupsGrid
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_USER_GROUPS_GRID)
export class GzExchangeProtectionUserGroupsGridWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/styles

  protected selectedItems: UserGroupGridItem[] = [];

  @query('#user-groups-grid')
  protected gridComponent: GridViewWebComponent<Column, ColumnKeys>;

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected columns: Column[] = getGridColumns();
  protected allColumnDetails: ColumnDetails<Column, ColumnKeys>[] = getGridColumnDetails();
  protected hasFilters = true;
  protected filters: Filter<ColumnKeys>[] = getFilters();
  protected predefinedFiltersKeys: ColumnKeys[] = getPredefinedFilterKeys();
  // page size settings
  protected pageSizeOptions: SelectOption[] = PAGE_SIZE_OPTIONS;
  protected pageSize = 10;
  protected hasSelectableRows = true;

  constructor() {
    super();
  }

  protected getDataProvider() {
    return new GzUserGroupsDataProvider();
  }

  static get styles() {
    return styles;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildGrid();
  }

  protected buildGrid() {
    this.gridComponent.viewId = VIEW_GRID_ID;

    this.gridComponent.allColumnDetails = this.allColumnDetails;
    this.gridComponent.columnRenderers = {
      [ColumnKeys.NAME]: nameRenderer.bind(this),
      [ColumnKeys.DESCRIPTION]: descriptionRenderer.bind(this),
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
    };

    this.gridComponent.columns = this.columns;
    this.gridComponent.isCompactViewChecked = false;
    this.gridComponent.hideHeaderActions = true;
    this.gridComponent.showGlobalActions = true;

    if (this.hasFilters) {
      this.gridComponent.filters = this.filters;
      this.gridComponent.removeFilters = false;
      this.gridComponent.predefinedFiltersKeys = this.predefinedFiltersKeys;
      this.gridComponent.filtersUiConfigs = [
        {
          key: ColumnKeys.COMPANY,
          config: {
            allowSelectNone: true,
          },
        },
      ];
    }
    this.gridComponent.defaultViewProps = {
      id: VIEW_GRID_ID,
      name: 'Default view',
      type: ViewType.DEFAULT,
      displayedColumns: this.columns,
      filters: this.filters,
      pageSize: DEFAULT_PAGE_SIZE,
      compactView: true,
    };

    // pagination settings
    this.gridComponent.hasPagination = true;
    this.gridComponent.pageSizeOptions = this.pageSizeOptions;
    this.gridComponent.pageSize = this.pageSize;
    this.gridComponent.dataProvider = this.getDataProvider();

    this.gridComponent.hasSelectableRows = this.hasSelectableRows;

    if (this.hasSelectableRows) {
      this.gridComponent.selectionStoreConfig = {
        functionalMode: SelectionStoreMode.RECORDS,
        maxNumOfItems: SELECTION_STORE_CONFIGURATION.maxNumOfItems,
      };
    }

    if (!this.gridComponent.started) {
      this.gridComponent.injectDependencies({
        translateFn: translateFn,
      });
      this.gridComponent
        .start()
        .catch(console.error)
        .then(() => {
          const gzImageRepo = this.gridComponent.shadowRoot?.querySelector(
            '.no-results gz-image-repo'
          ) as GzImageRepoWebComponent;
          gzImageRepo.state = ImageState.NO_IMAGE;
          gzImageRepo.textContent = '';
          const gridToolbar = this.gridComponent.shadowRoot?.querySelector('.grid-header__toolbar') as HTMLElement;
          gridToolbar.style.paddingTop = '0px';
        });
    }
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <div>
        <gz-button id="add-button" @click="${this.onAddItem.bind(this)}" type="outline"
          >${translateFn('Add')}
        </gz-button>
        <gz-button
          id="delete-button"
          type="outline"
          disabled="true"
          color="negative"
          @click="${this.onDeleteSelectedItemsWithDialog.bind(
            this,
            translateFn('Delete user groups?'),
            translateFn('You are about to delete the selected groups. This action cannot be undone. ')
          )}"
          >${translateFn('Delete')}
        </gz-button>
      </div>
    `;
  }

  protected onDeleteSelectedItemsWithDialog(title: string, message: string) {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<p>${message}</p>`;

    const footer = document.createElement('div');
    footer.innerHTML = `
      <gz-button id="buttonOne" type="outline" color="negative">${translateFn('Delete')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
  `;

    const footerBtnOne = footer.querySelector('#buttonOne') as GzButtonWebComponent;
    const footerBtnTwo = footer.querySelector('#buttonTwo');

    const dialogConfigs: GzDialog = {
      headline: title,
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', async () => {
        footerBtnOne.setLoadingState(true);
        await this.onDeleteSelectedItems();
        footerBtnOne.setLoadingState(false);
        dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
  }

  protected onAddItem(event: CustomEvent) {
    this.openEditUserGroupDialog();
  }
  protected openEditUserGroupDialog(editedItem?: UserGroupItem) {
    const addUserGroupItem = async (formValue: UserGroupItem): Promise<ValidationResult> => {
      try {
        const res = await this.getDataProvider().createRecord([formValue]);
        this.gridComponent.controller?.handleGridItemsLoad(true);

        return { valid: res.success };
      } catch (error) {
        console.error(error);
        // show error toast notification
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: translateFn(
            'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
          ),
          severity: ToastSeverity.ERROR,
        });

        return { valid: false, error: error.message };
      }
    };

    const editUserGroupItem = async (formValue: UserGroupItem): Promise<ValidationResult> => {
      try {
        const res = await this.getDataProvider().updateRecords([formValue]);
        this.gridComponent.controller?.handleGridItemsLoad(true);
        return { valid: res.success };
      } catch (error) {
        console.error(error);
        // show error toast notification
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: translateFn(
            'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
          ),
          severity: ToastSeverity.ERROR,
        });

        return { valid: false, error: error.message };
      }
    };

    const configs: FullPagePopupConfigs = {
      bodyForm: new GzEditUserGroupContainerWebComponent(editedItem),
      strings: {
        title: editedItem === undefined ? translateFn('Add user group') : translateFn('Edit user group'),
        confirm: editedItem === undefined ? translateFn('Add') : translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: editedItem === undefined ? addUserGroupItem : editUserGroupItem,
      shouldShowCancelConfirmation: true,
    };
    new GzPolicyFullPageWindowComplex(configs).show();
  }

  protected onEditRow(rowData: any) {
    // edit logic here
    this.openEditUserGroupDialog(rowData.item);
  }

  protected async onDeleteSelectedItems() {
    try {
      const res = await this.getDataProvider().removeRecords(this.selectedItems);
      this.gridComponent.controller?.handleGridItemsLoad(true);
    } catch (error) {
      console.error(error);
      // show error toast notification
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn(
          'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
        ),
        severity: ToastSeverity.ERROR,
      });
    }
  }
  render() {
    return html` <div>${this.renderGrid()}</div>`;
  }

  renderGrid() {
    return html`<gz-grid-view id="user-groups-grid" class="grid-view" @selectRows="${this.onSelectRows.bind(this)}">
      <div class="header-buttons" slot="grid-header-actions-slot">${this.renderHeaderButtons()}</div>
      <div slot="no-results--additional-data">${this.renderEmptyStateGrid()}</div>
    </gz-grid-view> `;
  }

  renderEmptyStateGrid() {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_13}" fontWeight="${FontWeights.LIGHT}"
        >${translateFn('No user groups')}</gz-text
      >

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddItem.bind(this)}"
        >${translateFn('Adjust your filters or start adding groups.')}
      </gz-button>`;
  }

  protected onSelectRows(event: CustomEvent) {
    this.selectedItems = event.detail.selectionStoreData;
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  public reset() {
    this.gridComponent.sortColumnName = undefined;
    this.gridComponent.sortDirection = undefined;
    this.gridComponent.controller?.discardViewChanges();
  }

  // see https://lit-element.polymer-project.org/guide/events
}

declare global {
  interface HTMLElementTagNameMap {
    GzExchangeProtectionUserGroupsGrid: GzExchangeProtectionUserGroupsGridWebComponent;
  }
}
